/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SYNCED_BUILDS, PRESETS, presetDates, utcDay, validateSearch, FILTER_GROUPS, FLAGS, focusItem,
  DEFAULT_FILTERS, compareVersionsDesc, MAX_RANGE_DAYS, paramValue,
} from './installsModel';
import { isoUtc } from './installsFormat';

/** A modal: closes on Escape, a backdrop click, or its own buttons. Focus moves inside on open. */
export function Modal({ title, onClose, wide = false, children, labelledBy }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const id = labelledBy || `inst-dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="inst inst-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`inst-dialog${wide ? ' inst-dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1} ref={ref}>
        <h2 id={id}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/* ── Search ── */

export function SearchDialog({
  search, versionsSeen, now, busy, onSubmit, onClose,
  maxDays = MAX_RANGE_DAYS,
  hint = `At most ${MAX_RANGE_DAYS} days. Only days synced on the BigQuery Events screen have data.`,
}) {
  // Refilled from the search on screen every time it opens, so Cancel needs no undo.
  const [form, setForm] = useState(() => ({ ...search }));
  const [error, setError] = useState('');
  const today = utcDay(now);
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setError(''); };
  const activePreset = PRESETS.find((p) => {
    const d = presetDates(p, now);
    return d.from === form.from && d.to === form.to;
  });

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    const msg = validateSearch(form, maxDays);
    if (msg) { setError(msg); return; }
    const rows = String(form.rows ?? '').trim();
    onSubmit({
      ...form,
      versions: String(form.versions ?? '').trim(),
      device: String(form.device ?? '').trim(),
      rows: /^\d+$/.test(rows) ? String(Math.min(5000, Math.max(1, Number(rows)))) : '',
    });
  };

  return (
    <Modal title="Search installs" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <div className="inst-form-row">
          <label className="inst-field">
            <span>Build</span>
            <select className="inst-select" value={form.build} onChange={(e) => set({ build: e.target.value })} aria-label="Build">
              <option value="">All builds</option>
              {SYNCED_BUILDS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </label>
          <div className="inst-field">
            <span>Days (UTC)</span>
            <div className="inst-presets" role="group" aria-label="Quick ranges">
              {PRESETS.map((p) => (
                <button type="button" key={p.key} aria-pressed={activePreset?.key === p.key} onClick={() => set(presetDates(p, now))}>{p.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input className="inst-input" style={{ width: 150 }} type="date" aria-label="From" max={today} value={form.from} onChange={(e) => set({ from: e.target.value })} />
              <span>→</span>
              <input className="inst-input" style={{ width: 150 }} type="date" aria-label="To" max={today} value={form.to} onChange={(e) => set({ to: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="inst-form-row">
          <label className="inst-field" style={{ flex: '1 1 140px' }}>
            <span>Version</span>
            <input className="inst-input" style={{ width: '100%' }} list="inst-versions" placeholder="e.g. 1.29.1, 1.28.3" value={form.versions} onChange={(e) => set({ versions: e.target.value })} aria-label="Version" />
            <datalist id="inst-versions">
              {[...versionsSeen].sort(compareVersionsDesc).map((v) => <option key={v} value={v} />)}
            </datalist>
          </label>
          <label className="inst-field" style={{ flex: '1 1 200px' }}>
            <span>Device</span>
            <input className="inst-input" style={{ width: '100%' }} placeholder="device_id or user_pseudo_id" value={form.device} onChange={(e) => set({ device: e.target.value })} aria-label="Device" />
          </label>
          <label className="inst-field" style={{ flex: '0 0 110px' }}>
            <span>Row cap</span>
            <input className="inst-input" style={{ width: '100%' }} type="number" min={1} max={5000} step={500} placeholder="1000" value={form.rows} onChange={(e) => set({ rows: e.target.value })} aria-label="Row cap" />
          </label>
          <div className="inst-field" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="inst-link" onClick={() => set({ versions: '', device: '', rows: '' })}>Clear these</button>
          </div>
        </div>
        <p className="inst-hint" style={{ margin: 0 }}>{hint}</p>
        <div className="inst-dialog-foot">
          {error && <span className="inst-form-error" role="alert">{error}</span>}
          <button type="button" className="inst-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="inst-btn inst-btn--primary" disabled={busy}>Show installs</button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Filters ── */

export function FiltersDialog({ filters, counts, onChange, onClose }) {
  const focus = filters.focus !== DEFAULT_FILTERS.focus ? focusItem(filters.focus) : null;
  const toggleFlag = (k) => onChange({ flags: filters.flags.includes(k) ? filters.flags.filter((x) => x !== k) : [...filters.flags, k] });
  const opt = (pressed, n, label, onClick, key) => (
    <button key={key} type="button" className={`inst-opt${n === 0 ? ' inst-opt--zero' : ''}`} aria-pressed={pressed} onClick={onClick}>
      {label}<span className="inst-opt-n">{n}</span>
    </button>
  );
  return (
    <Modal title="Filters" onClose={onClose}>
      {focus && <p className="inst-hint" style={{ margin: '0 0 6px' }}>Counts are within the story&apos;s current slice: {focus.story}.</p>}
      {FILTER_GROUPS.map((g) => (
        <section key={g.key}>
          <h3>{g.label}</h3>
          <div className="inst-pills">
            {g.options.map((o) => opt(filters[g.key] === o.value, counts[g.key][o.value], o.label, () => onChange({ [g.key]: o.value }), o.value))}
          </div>
        </section>
      ))}
      <section>
        <h3>Flags <span className="inst-hint">any of those picked</span></h3>
        <div className="inst-pills">
          {FLAGS.map((f) => opt(filters.flags.includes(f.key), counts.flags[f.key], f.label, () => toggleFlag(f.key), f.key))}
        </div>
      </section>
      <div className="inst-dialog-foot">
        <button type="button" className="inst-link" style={{ marginRight: 'auto' }} onClick={() => onChange({ install: 'any', status: 'any', account: 'any', flags: [] })}>Clear all</button>
        <button type="button" className="inst-btn inst-btn--primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

/* ── Flag explanation ── */

const FLAG_COPY = {
  'non-store': (r) => ({
    title: `Install source ${r.installSource}`,
    what: 'Pauser’s pauser_app_install reported where the app came from, and it was not an app store.',
    where: 'Android asks the OS for the installer package: Play gives play_store, Amazon gives amazon_store, the package installer, adb or none gives sideload, anything else gives other_store. iOS reads the receipt: sandbox gives testflight, real gives app_store, none gives unknown.',
    means: 'It almost always means a test or dev device, and is worth excluding from install counts.',
    facts: [['install_source', r.installSource], ['device_id', r.installDeviceId]],
  }),
  reinstall: (r) => ({
    title: `Opened fresh ${paramValue(r, 'previous_first_open_count')} times before`,
    what: 'Firebase attaches previous_first_open_count to every first_open by itself.',
    where: 'It increments on a reinstall or a Clear data, and survives uninstall.',
    means: '1–2 is an ordinary person. Dozens is a QA phone. It can exceed the number of real reinstalls.',
    facts: [['previous_first_open_count', paramValue(r, 'previous_first_open_count')]],
  }),
  'no-session': () => ({
    title: 'No session',
    what: 'The first open has no ga_session_id.',
    where: 'Firebase logs first_open when the process starts but opens a session only when the app reaches the foreground, so something started the app in the background and nobody opened it.',
    means: 'It usually comes with ✗ on every answer, which fill in if the person opens the app within the window.',
    facts: [['ga_session_id', null]],
  }),
  'sign-in-error': (r) => ({
    title: 'Sign-in error',
    what: 'A sign-in failed and none succeeded in the window.',
    where: 'The first failure’s event, method, info and message, as the app sent them.',
    means: 'custom_login_network_error is usually the phone, not Pauser. Messages are PII-scrubbed on the device.',
    facts: [
      ['event', r.signInError?.event], ['method', r.signInError?.method], ['info', r.signInError?.info],
      ['message', r.signInError?.message], ['at', r.signInErrorMillis != null ? `${isoUtc(r.signInErrorMillis)} UTC` : null],
    ],
  }),
  'sign-in-cancelled': (r) => ({
    title: 'Sign-in cancelled',
    what: 'custom_login_cancel: sent when the Google picker closes or Cancel is tapped on the Apple sheet.',
    where: 'The app, on the sign-in screen.',
    means: 'It is a choice, not a fault, though many on one method may point at a confusing sheet.',
    facts: [['method', r.signInCancelMethod], ['at', r.signInCancelMillis != null ? `${isoUtc(r.signInCancelMillis)} UTC` : null]],
  }),
};

export function FlagDialog({ flagKey, row, onClose }) {
  const c = FLAG_COPY[flagKey](row);
  return (
    <Modal title={c.title} onClose={onClose}>
      <p className="inst-hint" style={{ margin: '-6px 0 10px' }}>
        {row.deviceModel || 'Unknown device'} · {row.country || '—'} · first open {isoUtc(row.timestampMillis)} UTC
      </p>
      <dl className="inst-about">
        <dt>What it is</dt><dd>{c.what}</dd>
        <dt>Where it comes from</dt><dd>{c.where}</dd>
        <dt>What it usually means</dt><dd>{c.means}</dd>
      </dl>
      <dl className="inst-facts">
        {c.facts.map(([k, v]) => (
          <React.Fragment key={k}><dt>{k}</dt><dd>{v ?? '—'}</dd></React.Fragment>
        ))}
      </dl>
      {row.signedInUserId && (
        <p style={{ marginTop: 12 }}><Link to={`/user-detail/${btoa(String(row.signedInUserId))}`}>Open this member&apos;s User 360 →</Link></p>
      )}
      <div className="inst-dialog-foot"><button type="button" className="inst-btn inst-btn--primary" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

/* ── Events (static) ── */

const EVENT_GROUPS = [
  {
    title: 'Who is counted',
    rows: [
      ['first_open', 'Firebase', 'One row per install — the main table', 'previous_first_open_count, device_id'],
      ['pauser_app_install', 'Pauser', 'Install event answer; orphans when no first_open', 'install_source, device_id'],
    ],
  },
  {
    title: 'Steps',
    rows: [
      ['session_start', 'Firebase', 'Opened app', 'ga_session_id'],
      ['custom_login_start', 'Pauser', 'Sign-in started', 'method'],
      ['custom_login_start_firebase', 'Pauser', 'Sign-in started (returning email user)', 'method'],
      ['custom_sign_up_success', 'Pauser', 'Signed in — a new account', 'method'],
      ['custom_login_success', 'Pauser', 'Signed in — an existing account', 'method'],
    ],
  },
  {
    title: 'Why a sign-in stopped',
    rows: [
      ['custom_login_error', 'Pauser', 'Sign-in error flag', 'method, info, message'],
      ['custom_login_network_error', 'Pauser', 'Sign-in error flag', 'method, info, message'],
      ['custom_sign_up_error', 'Pauser', 'Sign-in error flag', 'method, info, message'],
      ['custom_login_cancel', 'Pauser', 'Sign-in cancelled flag', 'method'],
    ],
  },
];
export const EVENT_NAMES = EVENT_GROUPS.flatMap((g) => g.rows.map((r) => r[0]));

export function EventsDialog({ onClose, onCopy }) {
  return (
    <Modal title="Events this page reads" onClose={onClose} wide>
      {EVENT_GROUPS.map((g) => (
        <section key={g.title}>
          <h3>{g.title}</h3>
          <table className="inst-dtable">
            <thead><tr><th>Event</th><th>Sent by</th><th>Used for</th><th>Params</th></tr></thead>
            <tbody>
              {g.rows.map(([name, by, use, params]) => (
                <tr key={name}><td className="mono">{name}</td><td>{by}</td><td>{use}</td><td className="mono">{params}</td></tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      <p className="inst-hint" style={{ margin: '12px 0 0' }}>Not an event: <span className="mono">user_id</span> — any synced event carrying one counts as signed in.</p>
      <p className="inst-hint" style={{ margin: '4px 0 0' }}>Deliberately not read: Firebase&apos;s <span className="mono">login</span> and <span className="mono">sign_up</span> (also sent on failures).</p>
      <div className="inst-dialog-foot">
        <button type="button" className="inst-btn" onClick={() => onCopy(EVENT_NAMES.join('\n'))}>Copy names</button>
        <button type="button" className="inst-btn inst-btn--primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

/* ── About ── */

export function AboutDialog({ onClose, source = 'local' }) {
  return (
    <Modal title="About this page" onClose={onClose} wide>
      <dl className="inst-about">
        <dt>One row per install</dt>
        <dd>Every first_open in the range, the earliest per installation (user_pseudo_id). Firebase sends it on every install with no Pauser code involved, so it also counts the installs our own install event missed.</dd>
        <dt>The four answers</dt>
        <dd>Opened app (session_start), Install event (pauser_app_install), Sign-in started (custom_login_start or custom_login_start_firebase) and Signed in (custom_sign_up_success, custom_login_success, or any synced event carrying a user_id). Each cell shows how long after the first open the first such event came.</dd>
        <dt>The 48-hour window</dt>
        <dd>Every answer counts events from one minute before to 48 hours after that install&apos;s own first open — the same bar for an install on the first day of the range as on the last. The minute before is there because Firebase can stamp the first session_start a moment before first_open.</dd>
        <dt>Pending</dt>
        <dd>An install whose 48 hours run past the last synced day may still get answers, so a missing answer shows as pending, not ✗. A row with every answer in is final even inside its window.</dd>
        <dt>The story</dt>
        <dd>Four cumulative steps with the drop between each pair. A step minus the drop after it is always the next step. Click a step or drop to show only those rows; click it again to show all. The strip counts every other filter, so it keeps telling the whole story around the slice you picked.</dd>
        <dt>Sorting and filters</dt>
        <dd>Nothing re-runs the query after a search: filters, text search, sort and the exports all work on the rows already loaded. A missing date sorts as older than any real one. Flags are OR-ed: a row shows when it carries any flag picked.</dd>
        <dt>Same installation, not same phone</dt>
        <dd>Answers are matched on user_pseudo_id, one installation. device_id is never used: first_open often comes before the app sets it, and it survives a reinstall, so it would credit an earlier install&apos;s sign-in to this one.</dd>
        <dt>User</dt>
        <dd>The backend user id of the first synced event in the window that carried one. It links to that member&apos;s User 360 page.</dd>
        <dt>Flags</dt>
        <dd>Not a store install, opened fresh before, no session, sign-in error and sign-in cancelled. Click a flag for what it means and where it comes from. Error and cancel show only on installs that never signed in.</dd>
        <dt>Install events with no first open</dt>
        <dd>pauser_app_install events whose installation had no first_open from two days before the range to its end — in practice existing users on their first launch of a build that sends the event. They are listed under the table and not counted in the story.</dd>
        <dt>Days and times</dt>
        <dd>
          A GA4 day is a UTC day. Times show in UTC with your local time under them when it differs.{' '}
          {source === 'bigquery'
            ? 'The data comes straight from the GA4 export in BigQuery: finished days from their final tables, today while it is still streaming, so answers inside that window can still change. The query bar warns when a day in the range has no data yet.'
            : 'The data is the portal\u2019s copy of the GA4 export: only days synced on the BigQuery Events screen are here, and the query bar warns when a day in the range is not.'}
        </dd>
        <dt>Device</dt>
        <dd>The Device search matches a user_pseudo_id or the device_id the first open carried. It narrows the installs, not their answers.</dd>
      </dl>
      <div className="inst-dialog-foot"><button type="button" className="inst-btn inst-btn--primary" onClick={onClose}>Close</button></div>
    </Modal>
  );
}
